
import React, { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Icon } from '@/components/ui/icon'
import { Input } from '@/components/ui/input'
import type { Canvas } from '@/hooks/useCanvas'

interface CanvasRenameModalProps {
  isOpen: boolean
  onClose: () => void
  canvas: Canvas | null
  onRename: (canvasId: string, newTitle: string) => Promise<void>
}

export const CanvasRenameModal: React.FC<CanvasRenameModalProps> = ({
  isOpen,
  onClose,
  canvas,
  onRename,
}) => {
  const [title, setTitle] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  React.useEffect(() => {
    if (isOpen && canvas) {
      setTitle(canvas.title)
    }
  }, [isOpen, canvas])

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault()

    if (!canvas || !title.trim() || title === canvas.title) {
      return
    }

    setIsLoading(true)
    try {
      await onRename(canvas.uuid, title.trim())
      // onClose() is called by useCanvasModals hook
    } catch (error) {
      console.error('Failed to rename canvas:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <AlertDialogHeader>
            <AlertDialogTitle>Rename Session</AlertDialogTitle>
            <AlertDialogDescription>
              Enter a new name for &quot;{canvas?.title}&quot;
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4">
            <Input
              inputSize="md"
              placeholder="Session title"
              disabled={isLoading}
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel type="button" disabled={isLoading}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || !title.trim() || title === canvas?.title}
            >
              {isLoading ? (
                <>
                  <Icon name="loader-2" className="mr-2 h-4 w-4 animate-spin" />
                  Renaming...
                </>
              ) : (
                'Rename'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  )
}